import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function HammersmithAndFulhamCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Hammersmith & Fulham Pothole Claims | 676 Repairs in One Year | Fixtyer"
        description="Hammersmith & Fulham filled 676 potholes in 2023/24 on just 203km of roads — yet 13% of residential streets remain in RED condition and principal roads miss the 5% disrepair target. See what the DfT AMBER rating means for your claim."
        canonical="/councils/hammersmith-and-fulham"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Hammersmith & Fulham', url: '/councils/hammersmith-and-fulham' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Hammersmith & Fulham has GREEN spend — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because the council projects £1.88m capital spend against a £356k DfT allocation in 2025/26. But the overall rating is AMBER on condition — 13% of A-roads and 13% of U-roads were in RED condition in 2024/25. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential street?',
            answer: 'Unclassified roads make up 159.3km — 79% of the borough network. In 2024/25, 13% of U-roads were in RED condition (roughly 21km of residential streets). The council surveys U-roads annually using AI-driven LoHEG methodology, but only from 2023/24 onwards — pre-2023/24 inspector data cannot be compared with current figures.',
          },
          {
            question: 'Does the 676 pothole peak in 2023/24 help my claim?',
            answer: 'It can. Hammersmith & Fulham filled 676 potholes in 2023/24 — more than double any other year in its published data and against a network of just 202.6km. That is an admission the borough was producing defects at an elevated rate. If your incident falls in or near that period, the council\'s own repair volume supports an argument that defects were forming faster than inspections caught them.',
          },
          {
            question: 'The council says road condition is improving — does that weaken my claim?',
            answer: 'Not automatically. RED-condition U-roads did fall from 23% to 13% between 2023/24 and 2024/25, and A-road RED fell from 23% to 13% over three years. But the council still admits its Borough Principal Road Network misses the 5% disrepair target — 13% of A-roads remain in RED. Improvement at network level does not prove the specific pothole that damaged your car was reasonably known about and fixed.',
          },
          {
            question: 'Can the council use pre-2023/24 condition data in its Section 58 defence?',
            answer: 'The council itself states that before 2023/24, B/C and U-road condition surveys were completed internally by inspectors and thus cannot be compared with AI LoHEG surveys done since. For claims on residential streets, that limits how far back the council can point to consistent condition records — your prior reports and photos become more important.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Hammersmith & Fulham Council"
        councilSlug="hammersmith-and-fulham"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/hammersmith-and-fulham"
        headline="Hammersmith & Fulham Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Hammersmith & Fulham</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            676 Potholes in One Year on 203km of West London Roads
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Hammersmith & Fulham is one of London's smallest highway networks — just 202.6km — yet the council
            filled <strong className="text-rose-400">676 potholes in 2023/24 alone</strong>, more than double any
            other year on record. The DfT rates spend <strong className="text-emerald-400">GREEN</strong>, but the
            overall scorecard is <strong className="text-amber-400">AMBER</strong>: 13% of principal A-roads still
            miss the council's own 5% disrepair target, and 79% of the network is residential streets where
            13% remain in RED condition.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">676</div>
            <div className="text-white font-medium mt-2">Potholes filled in 2023/24</div>
            <div className="text-brand-300 text-sm mt-1">
              On a network smaller than most market towns — 202.6km total, with 159.3km of residential
              U-roads. The council expects 300–400 repairs in 2025/26, in line with historic trends.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Network Size */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A Borough-Sized Network
          </h2>
          <p className="text-surface-600 mb-8">
            Hammersmith & Fulham manages one of the smallest highway inventories in England — dense urban
            streets where every defect sits close to heavy traffic
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">26.8 km</div>
              <div className="text-sm text-surface-600 mt-1">A-roads (13.2%)</div>
              <div className="text-xs text-surface-500 mt-2">Bus routes, principal traffic</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-brand-900">16.7 km</div>
              <div className="text-sm text-surface-600 mt-1">B/C roads (8.2%)</div>
              <div className="text-xs text-surface-500 mt-2">Links between A and U roads</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
              <div className="text-3xl font-bold text-amber-700">159.3 km</div>
              <div className="text-sm text-amber-800 mt-1">U-roads (78.6%)</div>
              <div className="text-xs text-amber-700 mt-2">Mostly residential quieter roads</div>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 — Statutory Duty, Compact Network</h3>
            <p className="text-brand-200 mb-4">
              Hammersmith & Fulham acknowledges a statutory duty under Section 41 of the Highways Act 1980
              to manage and maintain the public highway. On a network this small, there is nowhere to hide:
              676 pothole repairs in a single year on 203km means defects were forming at a rate of roughly
              one repair every 300 metres of road, every year.
            </p>
            <p className="text-white font-medium">
              Your claim turns on whether the council's inspection regime caught the specific defect before
              it damaged your vehicle — not on borough-wide averages.
            </p>
          </div>
        </div>
      </section>

      {/* Condition Data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            AI-driven LoHEG survey data from the council's own transparency report — improving trends, but
            RED roads remain on every class
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (26.8km — 13% of network): improving but off-target</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">25%</td>
                    <td className="px-4 py-2 text-center">52%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">19%</td>
                    <td className="px-4 py-2 text-center">33%</td>
                    <td className="px-4 py-2 text-center">48%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">13%</td>
                    <td className="px-4 py-2 text-center font-bold">36%</td>
                    <td className="px-4 py-2 text-center font-bold">51%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED A-roads have halved in three years (23% → 13%). Credit where due. But the council's own
              target for the Borough Principal Road Network is 5% disrepair — and 13% of A-roads remain in
              RED condition, more than double that benchmark.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (16.7km — 8% of network): comparable data from 2023/24 only</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">17%</td>
                    <td className="px-4 py-2 text-center">32%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                  </tr>
                  <tr className="bg-emerald-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">11%</td>
                    <td className="px-4 py-2 text-center font-bold">32%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">57%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C RED fell from 17% to 11% in one year. The council switched from internal inspector surveys
              to AI LoHEG methodology in 2023/24 — earlier figures cannot be compared.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">U-roads (159.3km — 79% of network): where most claims happen</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">23%</td>
                    <td className="px-4 py-2 text-center">31%</td>
                    <td className="px-4 py-2 text-center">46%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">13%</td>
                    <td className="px-4 py-2 text-center font-bold">31%</td>
                    <td className="px-4 py-2 text-center font-bold text-emerald-600">56%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              RED U-roads fell sharply from 23% to 13% — but 13% of 159.3km is still roughly 21km of
              residential streets in major deterioration. Nearly a third of U-roads (31%) sit in AMBER,
              where maintenance may soon be required.
            </p>
          </div>

          <div className="space-y-6">
            <QuoteCard
              quote="As seen in Figure 1, the proportion of poor condition on A roads has reduced over the last 3 years with maintenance works being carried out. However, more investment is needed to bring the overall Borough Principal Road Network up to the set level of service of 5% disrepair."
              source="Hammersmith & Fulham Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Before 2023/24, the condition surveys were completed internally by inspectors and thus cannot be compared with condition surveys done since."
              source="Hammersmith & Fulham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            GREEN Spend on a Compact Network
          </h2>
          <p className="text-surface-600 mb-8">
            Hammersmith & Fulham projects more than five times its DfT capital allocation — yet reactive
            maintenance is rising again
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Resurfaced (km)</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£985k</td>
                  <td className="px-4 py-3 text-center">£1,456k</td>
                  <td className="px-4 py-3 text-center">40%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">60%</td>
                  <td className="px-4 py-3 text-center">5.0</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£1,785k</td>
                  <td className="px-4 py-3 text-center">£939k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">66%</td>
                  <td className="px-4 py-3 text-center">34%</td>
                  <td className="px-4 py-3 text-center">4.3</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center text-surface-400">—</td>
                  <td className="px-4 py-3 text-center">£1,899k</td>
                  <td className="px-4 py-3 text-center">£1,407k</td>
                  <td className="px-4 py-3 text-center">57%</td>
                  <td className="px-4 py-3 text-center">43%</td>
                  <td className="px-4 py-3 text-center">8.4</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£109k</td>
                  <td className="px-4 py-3 text-center">£2,653k</td>
                  <td className="px-4 py-3 text-center">£1,586k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">63%</td>
                  <td className="px-4 py-3 text-center">37%</td>
                  <td className="px-4 py-3 text-center">7.5</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£109k</td>
                  <td className="px-4 py-3 text-center">£2,254k</td>
                  <td className="px-4 py-3 text-center">£1,649k</td>
                  <td className="px-4 py-3 text-center">58%</td>
                  <td className="px-4 py-3 text-center">42%</td>
                  <td className="px-4 py-3 text-center">5.3</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-4 py-3 font-mono font-bold">2025/26 (proj.)</td>
                  <td className="px-4 py-3 text-center font-bold">£356k</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">£1,880k</td>
                  <td className="px-4 py-3 text-center font-bold">£1,610k</td>
                  <td className="px-4 py-3 text-center">54%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">46%</td>
                  <td className="px-4 py-3 text-center">5.4</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">Why Spend Is GREEN</h3>
              <p className="text-emerald-800 text-sm mb-3">
                The council projects £1.88m capital spend against a £356k DfT allocation in 2025/26 — over
                five times the central government grant. Combined capital and revenue spend reaches £3.49m
                on just 203km of roads.
              </p>
              <p className="text-emerald-800 text-sm">
                Additional TfL funding of up to £200k per year targets the Borough Principal Road Network
                routes in poorest condition.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Reactive Creep</h3>
              <p className="text-amber-800 text-sm mb-3">
                Reactive maintenance share is projected to rise to 46% in 2025/26 — up from 37% in 2023/24.
                The council is requesting additional capital funds to increase planned maintenance and
                reduce costlier reactive work.
              </p>
              <p className="text-amber-800 text-sm">
                In 2020/21, 60% of spend was reactive. The borough has been here before.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="We recently undertook an investment modelling exercise to review our highway maintenance spending against deterioration trends on the network and the challenges our roads and footways will face over the next 10 years. As a result, we are requesting additional capital funds to increase our planned maintenance activities and reduce the need for costlier reactive maintenance activities."
            source="Hammersmith & Fulham Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 676-Pothole Year
          </h2>
          <p className="text-surface-600 mb-8">
            Four years of published repair data — and one extraordinary spike
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">201</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">274</td>
                </tr>
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">676</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">314</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Four-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">1,465</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="In the last 5 years, we have filled around 1,500 potholes, with a peak of 676 potholes throughout 2023/24, as seen in Table 2. We expect to fill between 300 – 400 potholes this year, in line with historic trends."
              source="Hammersmith & Fulham Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">More Than Double Any Other Year</h3>
              <p className="text-surface-600 text-sm">
                The 676 repairs in 2023/24 are 2.5 times the 2022/23 figure and more than double 2024/25.
                On a 203km network, that is roughly 3.3 pothole repairs per kilometre in a single year —
                a network producing defects at an elevated rate, by the council's own count.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Claims Feed The Works Programme</h3>
              <p className="text-amber-800 text-sm">
                The council incorporates residents' claims and enquiries into its planned maintenance
                priority scoring. A prior report of the same defect is not just evidence for your claim —
                it is data the council says it uses to decide where to spend.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspection methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            How Hammersmith & Fulham Inspects Its Roads
          </h2>
          <p className="text-surface-600 mb-8">
            AI surveys, risk-based hierarchy, and the Section 58 questions they raise
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey Methodology</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• A-roads: yearly LoHEG driven surveys with a trained AI defect model</li>
                <li>• B/C and U-roads: annual AI surveys under LoHEG methodology from 2023/24</li>
                <li>• Invalid survey sections: less than 5% of the network (weather, lighting)</li>
                <li>• No A-road condition surveys in 2020 and 2021 due to Covid-19</li>
              </ul>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Safety Inspections</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• Risk-based Network Hierarchy per LoTAG guidance</li>
                <li>• Inspection frequency set by road risk and criticality</li>
                <li>• Defects raised with specific response times in the HMMP</li>
                <li>• All reactive jobs recorded in an Asset Management System</li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="Inspectors identify defects and raise repair orders with specific response times, as detailed in the Highways Maintenance Management Plan (HMMP). Reactive maintenance ensures serviceability and safety of the assets in line with our statutory duty to maintain the public highway under Section 41 of the Highways Act 1980 and all jobs are recorded in an Asset Management System to allow the Council to monitor the works carried out and understand maintenance needs."
            source="Hammersmith & Fulham Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="bg-brand-950 text-white rounded-2xl p-8 mt-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 58 — What To Ask</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Hammersmith & Fulham must show a reasonable system for
              knowing and fixing defects. On this borough's data, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road inspected at the frequency the HMMP requires for its risk category?</li>
              <li>• If 676 potholes were filled in 2023/24, why wasn't yours caught before it caused damage?</li>
              <li>• Did you report the defect before your incident — and was it incorporated into the priority score?</li>
              <li>• On a U-road, can the council only point to two years of comparable AI survey data?</li>
              <li>• With 46% reactive spend projected, was the repair a patch or a lasting treatment?</li>
            </ul>
            <p className="text-white font-medium">
              A council that records every job in an asset management system has the data to answer these
              questions — and you have the right to ask.
            </p>
          </div>
        </div>
      </section>

      {/* Forward plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Council Plans for 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            Resurfacing targets, climate resilience, and the gap between plan and daily defects
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">5.4 km</div>
              <div className="text-sm text-surface-600 mt-1">Carriageway resurfacing planned</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">17</div>
              <div className="text-sm text-surface-600 mt-1">Roads across borough wards</div>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200 text-center">
              <div className="text-3xl font-bold text-brand-900">300–400</div>
              <div className="text-sm text-surface-600 mt-1">Potholes expected to fill</div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 border border-surface-200 mb-8">
            <h3 className="font-semibold text-brand-900 mb-3">Resilience+ and Climate Pressure</h3>
            <p className="text-surface-600 text-sm mb-3">
              The council has defined a risk-based Resilience+ Network for winter gritting and emergency
              access. It also acknowledges that climate change is driving harsher winters, warmer summers,
              and short extreme rainfall events — all of which accelerate carriageway deterioration.
            </p>
            <p className="text-surface-600 text-sm">
              As Lead Local Flood Authority, the council is incorporating SuDS schemes to manage surface
              water. If your pothole formed after heavy rainfall, the drainage context may matter.
            </p>
          </div>

          <QuoteCard
            quote="Public Claims and Enquiries: We incorporate our residents' claims and enquiries to ensure spending is focused in areas of public interest"
            source="Hammersmith & Fulham Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend AMBER Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Hammersmith & Fulham invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend — capital investment over five times the DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Condition improving on all road classes in comparable survey data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual AI LoHEG surveys on A, B/C and U-roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented HMMP with specific defect response times
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Claims and enquiries formally incorporated into maintenance prioritisation
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence. Generic claims will struggle on principal roads.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 13% of A-roads and U-roads still in RED in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> BPRN misses the council's own 5% disrepair target (13% RED on A-roads)
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 676 pothole repairs in 2023/24 — defects forming faster than catch-up
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 79% of network is U-roads — roughly 21km still in RED condition
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pre-2023/24 B/C and U-road data not comparable — limits historical defence
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Reactive spend rising to 46% projected in 2025/26
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend and improving condition trends, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the 2023/24 spike and 13% RED rate are your strongest arguments</li>
              <li>• Timing relative to the 676-repair year if your incident falls in 2023/24</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Hammersmith & Fulham's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Hammersmith & Fulham?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded borough demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No 676-repair spike argument</li>
                <li>• No prior-report search</li>
                <li>• No U-road condition data cited</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 676 pothole peak documented</li>
                <li>✅ 13% RED U-road rate argued</li>
                <li>✅ BPRN 5% target miss cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to H&F</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=hammersmith-and-fulham"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Hammersmith & Fulham Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Hammersmith & Fulham has GREEN spend — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because the council projects £1.88m capital spend
                against a £356k DfT allocation in 2025/26. But the overall rating is AMBER on condition —
                13% of A-roads and 13% of U-roads were in RED condition in 2024/25. Section 58 turns on
                whether the specific defect that damaged your vehicle was reasonably inspected and repaired,
                not on aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential street?
              </h3>
              <p className="text-surface-600 text-sm">
                Unclassified roads make up 159.3km — 79% of the borough network. In 2024/25, 13% of U-roads
                were in RED condition (roughly 21km of residential streets). The council surveys U-roads
                annually using AI-driven LoHEG methodology, but only from 2023/24 onwards — pre-2023/24
                inspector data cannot be compared with current figures.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the 676 pothole peak in 2023/24 help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Hammersmith & Fulham filled 676 potholes in 2023/24 — more than double any other
                year in its published data and against a network of just 202.6km. That is an admission the
                borough was producing defects at an elevated rate. If your incident falls in or near that
                period, the council's own repair volume supports an argument that defects were forming faster
                than inspections caught them.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The council says road condition is improving — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not automatically. RED-condition U-roads did fall from 23% to 13% between 2023/24 and
                2024/25, and A-road RED fell from 23% to 13% over three years. But the council still admits
                its Borough Principal Road Network misses the 5% disrepair target — 13% of A-roads remain in
                RED. Improvement at network level does not prove the specific pothole that damaged your car
                was reasonably known about and fixed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Can the council use pre-2023/24 condition data in its Section 58 defence?
              </h3>
              <p className="text-surface-600 text-sm">
                The council itself states that before 2023/24, B/C and U-road condition surveys were
                completed internally by inspectors and thus cannot be compared with AI LoHEG surveys done
                since. For claims on residential streets, that limits how far back the council can point to
                consistent condition records — your prior reports and photos become more important.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.lbhf.gov.uk/sites/default/files/2025-07/lbhf-local-highways-maintenance-transparency-report-v1.0.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Hammersmith & Fulham Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
